/**
 * MIT License
 * Copyright (c) 2023 Jamasp Team
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useAlert } from '../../context/AlertContext';
import { db, functions } from '../../firebase';
import '../pages/admin.css';
import HealthPanel from '../components/HealthPanel';
import ParticipantProfile from '../components/ParticipantProfile';
import HealthInsights from '../components/HealthInsights';
import Breadcrumbs from '../components/Breadcrumbs';

// Fitbit-era cards (token introspection, Fitbit device list, old fetch history) were removed
// with the Fitbit Web API. Connection, devices and fetching are on the Google Health panel;
// a project's fetch history is on the project's Google Health tab.
export default function ShowParticipant() {
  const { participantId, projectId } = useParams();
  const navigate = useNavigate();
  const { addAlert } = useAlert();

  const [isLoading, setIsLoading] = useState(false);
  const [participantData, setParticipantData] = useState({});
  const [projectParticipants, setProjectParticipants] = useState([]);
  const [projectName, setProjectName] = useState('');

  useEffect(() => {
    if (!projectId) return;
    getDoc(doc(db, 'projects', projectId))
      .then((snap) => setProjectName(snap.exists() ? snap.data().name || '' : ''))
      .catch(() => {});
  }, [projectId]);

  const loadParticipant = () => getDoc(doc(db, 'participants', participantId))
    .then((snap) => {
      if (snap.exists()) setParticipantData(snap.data());
      else addAlert('error', `Participant ${participantId} not found`);
    })
    .catch((err) => console.error('Error getting participant:', err));

  useEffect(() => {
    setIsLoading(true);
    getDoc(doc(db, 'participants', participantId))
      .then((snap) => {
        if (snap.exists()) setParticipantData(snap.data());
        else addAlert('error', `Participant ${participantId} not found`);
      })
      .catch((err) => console.error('Error getting participant:', err))
      .finally(() => setIsLoading(false));
  }, [participantId, addAlert]);

  useEffect(() => {
    const getProjectParticipants = httpsCallable(functions, 'getProjectParticipants');
    getProjectParticipants({ projectId, activeOnly: true })
      .then((r) => setProjectParticipants(r.data?.participants || []))
      .catch(() => addAlert('error', 'Error loading project participants'));
  }, [projectId, addAlert]);

  const health = participantData?.health;

  return (
    <div className="ja-root">
      <div className="ja-page">

        <div className="ja-page-header">
          <div>
            <Breadcrumbs items={[{ label: 'Dashboard', to: '/admin' },
              ...(projectId ? [{ label: projectName || 'Project', to: `/admin/showProject/${projectId}` }] : []),
              { label: 'Participant' }]} />
            <h1 className="ja-page-title"><strong>Participant details</strong></h1>
            <p className="ja-page-subtitle" style={{ fontFamily: 'var(--jp-mono)', fontSize: 12 }}>{participantId}</p>
          </div>
          <div className="ja-btn-row">
            <button className="ja-btn ja-btn-ghost" onClick={() => navigate(-1)}>← Back</button>
          </div>
        </div>

        {isLoading && <div className="ja-progress"><div className="ja-progress-bar" /></div>}

        <ParticipantProfile participantId={participantId} projectId={projectId}
          participant={participantData} onChanged={loadParticipant} />

        <HealthPanel participantId={participantId} projectId={projectId} health={health} />

        {projectParticipants.length > 0 && (
          <div className="ja-card" style={{ marginBottom: 20 }}>
            <div className="ja-field" style={{ marginBottom: 0 }}>
              <label>Switch participant</label>
              <select
                className="ja-select"
                style={{ maxWidth: 360 }}
                value={participantId}
                onChange={(e) => navigate(`/admin/participants/${e.target.value}/project/${projectId}`)}
              >
                {projectParticipants.map((p) => (
                  <option key={p.uid} value={p.uid}>{p.uid}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div className="ja-card" style={{ marginTop: 20 }}>
          <div className="ja-card-header">
            <h3 className="ja-card-title">Insights</h3>
          </div>
          <HealthInsights projectId={projectId} participantIds={[participantId]} single />
        </div>

      </div>
    </div>
  );
}
